/**
 * VH-36: Reusable translation cache hook.
 *
 * Replaces the duplicated useEffect translation pattern found across
 * Supplements, TrainingTemplates, ExerciseLibrary, and other pages.
 *
 * Usage:
 *   const { getTranslation } = useTranslationCache({
 *     items: exercises,
 *     prefix: 'exercise',
 *     fetchTranslation: (id, lang) => workoutPlannerAPI.translateExercise(id, lang),
 *   })
 *
 *   // In render:
 *   const name = getTranslation(exercise.id, 'name') || exercise.name_en
 */

import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { contentLanguage } from '../i18n/supported'

/**
 * Hook that fetches and caches translations for a list of items.
 *
 * @param {Object} config
 * @param {Array} config.items - Array of items to translate (must have .id)
 * @param {string} config.prefix - Cache key prefix (e.g. 'template', 'exercise')
 * @param {Function} config.fetchTranslation - async (id, language) => response with .data
 * @param {boolean} [config.enabled=true] - Whether to fetch translations
 * @returns {{ translations: Object, getTranslation: Function }}
 */
export default function useTranslationCache({ items = [], prefix, fetchTranslation, enabled = true }) {
  const { i18n } = useTranslation()
  const currentLanguage = contentLanguage(i18n?.language)
  const [translations, setTranslations] = useState({})

  useEffect(() => {
    if (!enabled || currentLanguage === 'en' || items.length === 0) return

    let isMounted = true

    const fetchAll = async () => {
      const toFetch = items.filter(item => {
        const cacheKey = `${prefix}-${item.id}-${currentLanguage}`
        return !translations[cacheKey]
      })

      if (toFetch.length === 0) return

      const results = await Promise.allSettled(
        toFetch.map(async (item) => {
          const response = await fetchTranslation(item.id, currentLanguage)
          return { id: item.id, data: response.data }
        })
      )

      if (!isMounted) return

      const newTranslations = {}
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          const cacheKey = `${prefix}-${result.value.id}-${currentLanguage}`
          newTranslations[cacheKey] = result.value.data
        }
      })

      if (Object.keys(newTranslations).length > 0) {
        setTranslations(prev => ({ ...prev, ...newTranslations }))
      }
    }

    fetchAll()

    return () => { isMounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- translations state would cause infinite loop
  }, [items, currentLanguage, prefix, enabled])

  /**
   * Get a translated field value for an item.
   * Returns undefined if no translation is cached (caller should provide fallback).
   *
   * @param {number|string} id - Item ID
   * @param {string} field - Field name in the translation response (e.g. 'name', 'description')
   * @returns {string|undefined}
   */
  const getTranslation = useCallback((id, field) => {
    if (currentLanguage === 'en') return undefined
    const cacheKey = `${prefix}-${id}-${currentLanguage}`
    return translations[cacheKey]?.[field]
  }, [translations, currentLanguage, prefix])

  return { translations, getTranslation }
}
